import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import {initReactI18next} from 'react-i18next';

const resources = {
    en: {
        translation: {
            loading: {
                checkingLogin: 'Checking your login...',
                loadingApplication: 'Loading application...',
                loadingInterface: 'Loading interface...',
                loginScreen: 'Not logged in, navigating to login screen...',
                invalidDomain: 'No application found at this domain.',
                loadingError: 'Error loading application',
            },
            signalRStatus: {
                connecting:
                    'Connecting to server... Live updates will not be displayed until the connection is completed.',
                disconnected:
                    'Disconnected from server. Refresh the page to reconnect. Live updates will not work until the connection is restored.',
                reconnecting:
                    'Trying to reconnect to the server. Live updates will not be displayed until the connection is restored.',
                reconnected: 'The connection is restored, you are back online.',
                failedToConnect: 'Failed to connect to the server',
            },

            versionUpdate: {
                major: 'A major version update is available! Refresh the page as soon as possible, the application may not work well until you refresh.',
                minor: 'A version update is available! Please refresh the page soon to get access to new features.',
                patch: 'A small version update is available! At your convenience, please refresh the page to get the new version.',
                revision:
                    'A small version update is available! At your convenience, please refresh the page to get the new version.',
            },

            generalShared: {
                submitButton: 'Submit',
                loading: 'Loading...',
                editWithFormatter: 'Edit - {{formatterName}}',
                edit: 'Edit',
                delete: 'Delete',
                disable: 'Disable',
                clear: 'Clear',
                clearAll: 'Clear all',
                save: 'Save',
                addAnother: 'Save and add another',
                cancel: 'Cancel',
                close: 'Close',
                add: 'Add',
                addNew: 'New',
                apply: 'Apply',
                download: 'Download',
                view: 'View',
                areYouSure: 'Are you sure?',
                yesImSure: "Yes, I'm sure",
                exportToExcel: 'Export to Excel',
                exportToExcelRunning: 'Export is running, please wait',
                cannotDelete: "Can't delete this entity: it is in use",
                none: 'None',
                saved: 'Saved',
                peek: 'View',
                openInThisWindow: 'Open in this tab',
                openInNewWindow: 'Open in new tab',
                filter: 'Filter on {{columnName}}',
                time: 'Time',
                back: 'Back',
                reload: 'Refresh page',
                dismiss: 'Dismiss',
                selectAll: 'Select all',
                deSelecttAll: 'Deselect all',
                remove: 'Remove',
                copyPassword: 'Copy password',
            },
            errors: {
                notfound: "404 - Couldn't find data.",
                unauthorized: '401 - You must be logged in to view this data.',
                forbid: "403 - You don't have access to this data",
                general: 'Something went wrong',
                entitygone: '410 - This data has been deleted.',
                required: 'This field is required',
                minLength: 'The field filled in is too short',
                invalidInputLayout: 'Invalid format',
                notAnArray: 'Must be an array',
                notAnObject: 'Must be an object',
                notAValue: 'Must be a value',
                notAnFk: 'Must be a reference',
                invalidFK: 'Invalid reference',
                invalidEnum: 'Invalid option',
                notAString: 'Must be a string',
                notAnInteger: 'Must be an integer',
                notADecimal: 'Must be a decimal number',
                notABoolean: 'Must be a boolean',
                notADate: 'Must be a date',
                errorLoadingEntities: 'Failed to load data. Try again, or modify your filters.',
                actionNotAllowed: 'You are not allowed to perform this action',
                invalidEmail: 'Must be a valid email address',
            },
            booleanValuesFilter: {
                true: 'True',
                false: 'False',
            },
            dateTimeFilter: {
                before: 'before',
                after: 'after',
                between: 'between',
                empty: 'empty',
            },
            numericValuesFilter: {
                lessThen: 'less then',
                greaterThen: 'greater then',
                equal: 'equal',
            },
            enumRadioFilter: {
                noOptions: 'No options',
            },
            searchableDropdown: {
                placeholder: 'Type to search',
                noOptionFormatter: 'This filter is misconfigured, contact your admin',
            },
            accountShared: {
                userNameLabel: 'Username',
                passwordLabel: 'Password',
                totpLabel: 'TOTP code',
                backToLogin: 'Go to the login screen.',
                enterPassword: 'Enter your new password below.',
                passSetError: 'Cannot set password',
            },
            loginscreen: {
                welcome: 'Welcome to',
                title: '{{applicationName}}',
                loginError: 'Cannot log in',
                signInButton: 'Sign in',
                forgotPasswordButton: 'Forgot password?',
                forgotTwoFactorButton: 'Forgot Two-Factor?',
                addNewEmailButton: 'Add new email address',

                sso: {
                    or: 'or',
                    continueWith: 'Continue with {{provider}}',
                    error: 'Something went wrong while trying to log in with {{provider}}. Please try again.',
                },
            },
            forgotPassword: {
                title: 'Forgot your password?',
                titleAfter: "You've got mail",
                description: 'Enter your username below, and we will send you an email with instructions',
                passResetError: 'Cannot send password reset email',
                messageAfter: 'We have sent you an email with further instructions. You may close this window.',
            },
            forgotPasswordAndTotp: {
                title: 'Lost account information?',
                description:
                    'You have indicated that you lost both your password as well as your two-factor code. Unfortunately it is not possible in that case to recover your account in an automated manner. Contact your system administrator.',
            },
            recoverPassword: {
                title: 'Enter a new password',
                recoverError: 'Cannot set password',
                titleAfter: 'Password saved',
                messageAfter: 'We have saved your new password.',
            },
            activateAccount: {
                title: 'Activate your account',
                titleAfter: 'Account activated',
                messageAfter: 'Thank you for activating your account. You can now log in.',
            },
            setupTwoFactor: {
                title: 'Your account requires two factor authentication',
                subtitle:
                    "To make your account more secure a two step verification login is required. Click on 'Enable 2FA' to set it up.",
                generateSecretBtn: 'Enable 2FA',
                totpGeneratedDescription:
                    'Two-factor authentication has been enabled on your account. Set it up in a suitible authenticator app (such as Google Authenticator) using the code below.',
                toLoginBtn: 'To login',
                totpLabel: 'Secret code',
            },
            forgotTwoFactor: {
                title: 'Lost your 2FA?',
                description:
                    'If you lost access to your two-factor authentication, you can restore access to your account using an email. After clicking the button below, you will receive an email with further instructions for restoring your access.',
                sendRecoveryEmailBtn: 'Send recovery email',
                sendRecoveryEmailError: 'We could not send a recovery email',
                titleAfter: "You've got mail",
                messageAfter: 'We have sent a recovery email, check you inbox',
            },
            resetTwoFactor: {
                loadingTitle: 'Disabling your 2FA',
                successTitle: 'All done',
                errorTitle: 'Something went wrong',
                loadingDescription: 'The procedure to disable your 2FA is on going',
                errorDescription:
                    'Disabling two-factor authentication has failed. The error is: {{error}}. Try again, or contact an administrator.',
                successDescription:
                    'Two-factor authentication has been disabled on your account. Click the link "To login screen" below to return to the login screen. There, you can log in with your email address and password.',
            },
            sso: {
                activateTitle: 'Register using {{provider}}',
                activateSubtitle: 'A new {{applicationName}} account has been made for you.',
                activateDescription:
                    'Press the button below to login with your {{provider}} account to activate your account.',

                linkTitle: 'Log in with {{provider}}',
                linkSubtitle:
                    'You have requested to be able to log in to {{applicationName}} using your {{provider}} account.',
                linkDescription: 'Press the button below to login with your {{provider}} account to link your account.',

                unknownConfiguration: 'Unknown Single Sign-On configuration',

                loginWithProviderFailed: 'Login with {{provider}} failed',
                loginWithProviderSuccess: 'Login with {{provider}} was succesful',
                backToLogin: 'Back to login page',
                noSsoProviders: 'There are no Single Sign-On providers registered for this application',
            },
            confirmButton: {
                title: 'Are you sure?',
                yes: "Yes, I'm sure",
                no: 'No',
                cancel: 'Cancel',
                back: 'Go back',
            },
            emptyStatePage: {
                title: 'Choose an entity from the menu on the left',
            },
            entitiesList: {
                error: 'Error: no list display configuration for entity type',
                exportButton: 'Export to Excel',
            },
            entityCreatePage: {
                title: ' - create new',
            },
            entityDependencies: {
                title: 'Linked entities',
            },
            entityDetailPage: {
                title: ' - details',
                error: 'Error: no details display configuration for this entity type',
                noFormatters: 'Error: no detail view available for this data',
                formatterNotAvailable: 'Error: the chosen detail view is not available for this data',
                chooseFormatter: 'Choose how you want to view this data',
                backTo: 'To list of',
                entityOutdated: 'The data you are looking at is outdated',
                reloadButton: 'Reload',
            },
            entitymeta: {
                creation: 'Created by {{who}} on {{when, datetime}}',
                edited: 'Edited by {{who}} on {{when, datetime}}',
            },
            entityEditForm: {
                title: '- edit',
                error: 'Error: no editor configuration for this entity type',
                chooseFormatter: 'Choose the edit page',
                notAllowed: 'You do not have access to this page',
            },
            filterPanel: {
                addFilter: 'Add filter',
                advancedFilters: 'Advanced filters',
                chooseFilter: 'Choose filters',

                applyFilter: 'Apply filter',
                refresh: 'Refresh',
                openAccordion: 'View filter options',
                search: 'Search',
            },
            filterPopup: {
                title: 'Filter by {{attributeName}} - ({{selectedCount}} selected)',
                applyButton: 'Apply',
                selected_zero: 'None selected',
                selected_one: 'One selected',
                selected_other: '{{count}} selected',
            },
            accountSettings: {
                title: 'My account settings',
                userInfo: 'User information',
                userNamePassword: 'Username and password',
                emailLabel: 'Email address',
                passwordLabel: 'Password',
                apiLogin: 'API key',
                key: 'Key',
                copyKey: 'Copy key',
                twoFactorTitle: 'Two-factor authentication',
                activateTotp: 'Activate two-factor',
                showQR: 'Show QR-code',
                disableTotp: 'Disable two-factor',
                totpActive: 'Two-factor active',
                totpInactive: 'Two-factor inactive',
                twoFactorEnabledDescription:
                    "Two-factor authentication is enabled on your account. If you want to add your account to another authenticator app, click the 'Show secret' button below.",
                twoFactorDisableDescription:
                    "You can disable two-factor authentication by clicking the 'Disable 2FA' button below. Note: this makes you account less secure.",
                twoFactorCallToAction:
                    'You can make your account more secure by setting up two-factor authentication. Make sure you have an appropriate application for this, such as Google Authenticator. Then click the button below.',
                twoFactorCreateButton: 'Start setup',
                twoFactorDisableButton: 'Disable 2FA',
                twoFactorShowSecret: 'Show secret',
                twoFactorSecretDescription:
                    'Two-factor authentication is now enabled on your account. Scan the QR code below with the application you want to use.',
                qrCodeWarning:
                    'Warning! Scan this QR code immediately, it will be shown only once. If you do not scan this code, you will no longer be able to log in.',
                twoFactorHideSecret: 'Hide secret',
                noPasswordLogins: 'There are no username-password logins configured for your account',
                noApiLogins: 'There are no API logins configured for your account',
                hide: 'Hide',
                show: 'Show',
                userStatuses: {
                    Active: 'Active',
                    Blocked: 'Blocked',
                    Pending: 'Pending',
                },
                statusActive: 'Active',
                statusBlocked: 'Blocked',
                statusPending: 'Pending',
                statusUnlinked: 'Unlinked',
                sso: {
                    title: 'Single Sign-On',
                    noLogins: 'There are no Single Sign-On logins configured for your account',
                    add: 'Setup Single Sign-On',
                    link: 'Link {{provider}} account',
                    remove: 'Remove {{provider}} login',
                    confirmRemove:
                        'Are you sure that you want to remove the Single Sign-On login with provider <strong>{{provider}}</strong> and email <strong>{{email}}</strong>? You will no longer be able to use this <strong>{{provider}}</strong> account to sign in.',
                    confirmRemoveExtra:
                        'You will be signed out and will need to use a different login method to sign in again.',

                    loginAdded: 'Login added. A mail has been sent to you to activate it.',
                    activeLoginRemoved: 'Login removed successfully. You will now be logged out.',
                    inactiveLoginRemoved: 'Login removed successfully.',
                    emailSent: 'Email sent. Check the mail in your mailbox to proceed.',
                },
                loginMethodNotExist: 'Could not find login method.',
                passwordLoginDelete: {
                    confirmTitle: 'Remove email/password login?',
                    confirmRemove:
                        'Are you sure that you want to remove the email/password login method for {{userDisplayName}}? ' +
                        'They will no longer be able to use {{email}} to log into {{application}}.',
                    noOtherLoginMethods: 'User must have at least one SSO login method.',
                },
                ssoLoginDelete: {
                    confirmTitle: 'Remove {{provider}} login?',
                    confirmRemove:
                        'Are you sure that you want to remove the Single Sign-On login with provider {{provider}} and email {{providerEmail}}? ' +
                        'They will no longer be able to use {{provider}} to log into {{application}}.',
                    noOtherLoginMethods: 'User must have at least one other password or SSO login method.',
                    ssoNotFound: 'Could not find SSO configuration.',
                },
                apiLoginDelete: {
                    confirmTitle: 'Remove API key?',
                    confirmRemove:
                        'Are you sure that you want to remove your API key? ' +
                        'Any third-party applications that are using this API key to connect to {{application}} will no longer have access.',
                },
                loading: {
                    addingLogin: 'Adding login',
                    sendingEmail: 'Sending email',
                    removingSsoLogin: 'Removing Single Sign-On login',
                    removingPasswordLogin: 'Removing email/password login',
                    removingApiLogin: 'Removing Api key login',
                    addingApiKey: 'Adding API key login',
                    addingPasswordlogin: 'Adding email/password login',
                },
                success: {
                    passwordLoginDeleted:
                        'The email/password login method was removed. ' +
                        'You will no longer be able to use {{email}} to log into {{application}}.',
                    passwordLoginAdded:
                        'The email/password login method has been added. ' +
                        'You will recieve an activation email to set up a password at {{email}}.',
                    ssoLoginDeleted:
                        'The Single Sign-On login with provider {{provider}} and email {{providerEmail}} was removed. ' +
                        'They will no longer be able to use {{provider}} to log into {{application}}.',
                    apiLoginDeleted:
                        'The API key was deleted. ' +
                        'Any third-party applications that are using this API key to connect to {{application}} will no longer have access.',
                    apiLoginAdded: 'The API key was added.',
                },
            },
            modalApiLoginAdd: {
                title: 'Add API key',
                description: 'This action will add a new API key login to this user. Add?',
            },
            modalSsoLoginAdd: {
                title: 'Add Single Sign-On login',
                ssoProvider: 'Single Sign-On Provider',
                descriptionManyProviders:
                    'The invitation link to create a Single Sign-On login through the selected provider will be sent to the provided email address:',
                descriptionOneProvider:
                    'The invitation link to create a Single Sign-On login through <strong>{{providerName}}</strong> will be sent to the provided email address:',
                selectSsoProvider: 'Select Single Sign-On provider',
            },
            modalPasswordLoginAdd: {
                title: 'Add Email/Password login',
                description:
                    'Enter the email address you want to use for logging in below. An email will be sent to this address with an activation link. Click on that link to set a password and activate the login.' +
                    '<p><strong>Note:</strong> when you click the activation link, you will be logged out of your current session.</p>',
            },
            nav: {
                menuCategoryUsers: 'Users',
                menuLabelUsers: 'Users',
                menuCategoryAccount: 'Account',
                menuLabelSignOut: 'Sign out',
                menuLabelSettings: 'My account settings',
                menuSwitchAdmin: 'Switch to admin',
                menuSwitchData: 'Switch to data',
                viewDetail: 'view →',
                viewDetailPage: 'view detail page →',
                chooseRole: 'Choose user role',
                userRole: 'User role',
                adminSettings: 'Admin settings',
                navigation: 'Navigation',
                state: {compact: 'Expand menu', normal: 'Collapse menu'},
                theme: 'Theme',
                themeMode: {light: 'Light', dark: 'Dark'},
            },
            paging: {
                noMoreToLoadView:
                    '$t(paging.showingFormatted, {"count": {{showingFormatted}}}) $t(paging.total, {"count": {{total}}})',
                loadMore: 'Load more $t(paging.showing, {"count": {{showing}}}) $t(paging.total, {"count": {{total}}})',
                showingFormatted: 'Showing {{count}}',
                showing: '- showing {{count}}',
                total_zero: '',
                total_other: ' of {{count}}',
            },
            arrayEditor: {
                addNew: 'Add new {{typeName}}',
            },
            textboxFilter: {
                containsValue: 'Contains',
                equalsValue: 'Equals',
                startsWithValue: 'Starts with',
            },
            boolEditor: {
                yes: 'Yes',
                no: 'No',
            },
            entityTable: {
                noResults: 'There are no matching entities',
                saveColumnSelection: 'Save',
                noColumns: 'No data can be shown as this page has no columns',
            },
            userCreateModal: {
                title: 'Add new user',
                firstNameInputLabel: 'First name',
                lastNameInputLabel: 'Last name',
                loginSettingDropdownLabel: 'Login settings',
                loginSettingDropdownOptions: {
                    apiKey: 'API key',
                    usernamePassword: 'Username and password',
                    sso: 'SSO',
                },
                userRoleDropdownLabel: 'User role(s)',
                userRoleDropdownPlaceholder: 'Choose one or more user role(s)',
                userRoleDropdownConfigurationsRequiredTitle: 'Configuration required!',
                userRoleDropdownConfigurationsRequiredText:
                    'You’ve selected a role. To complete the process and assign permissions, configure the role details after this step. Without this, the user won’t have the assigned permissions.',
                userRoleDropdownNoConfigurations: 'None of the selected roles require configuration',
                userRoleDropdownNoConfiguration: 'The selected role does not require configuration',
                ssoProvidersRadioLabel: 'Log in with',
                successToastMessage: 'User {{firstName}} {{lastName}} has been successfully created.',
                successToastMessageWithConfig:
                    'User {{firstName}} {{lastName}} has been created, but one or more selected roles have missing configurations. Make sure to add configurations to complete the process.',
                applicationRoleRequired: 'Choose at least one role',
                chooseApplicationRole: 'An application role is required',
                firstNameRequired: 'A first name is required',
                lastNameRequired: 'A last name is required',
                emailRequired: 'An email is required',
            },
            userDetailPage: {
                title: 'User detail',
                backToList: 'Back to list',
                roleNotFound: 'Role not found',
                userInfo: 'User information',
                userName: 'User name',
                userStatus: 'User status',
                userRegistrationStatus: 'Registration status',
                neverLoggedIn: 'never logged in',
                lastLogin: 'last login: {{loginTime}}',
                blockUser: 'Blokkeer gebruiker',
                approveUser: 'Approve user',
                unblockUser: 'Unblock user',
                approveUserConfirmText: 'Are you sure you want to approve this user?',
                blockUserConfirmText: 'Are you sure you want to block this user?',
                unBlockUserConfirmText: 'Are you sure you want to unblock this user?',
                applicationRoles: 'Application Roles',
                assignApplicationRole: 'Assign application role',
                applicationRoleNotConfigured: 'Configuration missing - the role is still not configured',
                deleteUserSuccessMessage: 'The user has been deleted',
                successToastMessage: 'The selected roles have been assigned to {{firstName}} {{lastName}}.',
                successToastMessageWithConfig:
                    'The selected roles have been assigned to {{firstName}} {{lastName}}, but one or more roles have no configuration. Make sure to add the configuration to complete the process.',
                noApplicationRoles: 'There are no roles assigned to this user. As a result, this user cannot log in.',
                noDetailFormatter:
                    'The data cannot be visualized. Either there is no detail formatter for the profile of this role or no attributes have been added to the formatter.',
                noConfigurableAttributes:
                    'The data cannot be edited. No attributes have been added to the edit formatter of this role’s profile.',
                removeProfileData: 'Remove profile data',
                editProfileData: 'Edit profile data',
                applicationRoleAdded: 'Application role added',
                applicationRoleDeleted: 'Application role deleted',
                profileDataSaved: 'Profile data saved',
                loginSettings: {
                    title: 'Login settings',
                    apiKeyLogin: 'API key login',
                    noApiKeyLogins: 'There are no API key logins configured for this account',
                    deleteApiKey: {
                        confirmTitle: 'Remove API key?',
                        confirmRemove:
                            'Are you sure that you want to remove this API key for {{userDisplayName}}?' +
                            ' Any third-party applications that are using this API key to connect to {{application}} will no longer have access.',
                    },
                    sso: {
                        title: 'Single Sign-On',
                        noLogins: 'There are no Single Sign-On logins configured for this account',
                        remove: 'Remove {{provider}} login',
                        confirmRemove:
                            'Are you sure that you want to remove the Single Sign-On login with provider <strong>{{provider}}</strong> and email <strong>{{email}}</strong> for <strong>{{userDisplayName}}</strong>? They will no longer be able to use this <strong>{{provider}}</strong> account to sign in.',
                        confirmRemoveExtra:
                            'The user will be signed out and will need to use a different login method to sign in again.',
                        emailSent: 'Email sent. The user needs to check their mailbox to proceed.',
                        loginAdded: 'Login added. An email was to {{emailAddress}} to activate it.',
                        loginRemoved: 'Login removed successfully.',
                    },
                },
                loading: {
                    addingLogin: 'Adding login',
                    sendingEmail: 'Sending email',
                    removingSsoLogin: 'Removing Single Sign-On login',
                    removingPasswordLogin: 'Removing email/password login',
                    removingApiLogin: 'Removing Api login',
                    addingApiKey: 'Adding API key login',
                    addingPasswordlogin: 'Adding email/password login',
                },
                success: {
                    passwordLoginDeleted:
                        'The email/password login for {{userDisplayName}} was removed. ' +
                        'They will no longer be able to use {{email}} to log into {{application}}.',
                    passwordLoginAdded:
                        'The email/password login method has been added. ' +
                        'You will recieve an activation email to set up a password at {{email}}.',
                    ssoLoginDeleted:
                        'The Single Sign-On login with provider {{provider}} and email {{providerEmail}} was removed. ' +
                        'They will no longer be able to use {{provider}} to log into {{application}}.',
                    apiLoginDeleted:
                        'The API key for {{userDisplayName}} was deleted. ' +
                        'Any third-party applications that are using this API key to connect to {{application}} will no longer have access.',
                    apiLoginAdded: 'The API key for {{userDisplayName}} was added.',
                },
            },
            userEditPage: {
                title: '- edit',
                backToList: 'Back to list',
            },
            userRegistrationStatus: {
                completed: 'Completed',
                pendingApproval: 'Waiting for approval',
                pendingLogin: 'Waiting for login activation',
                pendingRole: 'Waiting for role assignment',
                revoked: 'Revoked',
            },
            userEditForm: {
                basicInfo: 'Basic information',
                profileInfo: 'Profile information',
                applicationRoleConfiguration: 'Role configuration',
                loginInfo: 'Login information',
                loginTypeLabel: 'Type of login',
                loginTypeUsername: 'Username and password',
                loginTypeApi: 'API key',
                loginTypeOpenId: 'OpenId Connect',
                emailLabel: 'Email address',
                applicationRoleLabel: 'Application Role',
                roleDropdown: '-- choose a role --',
                statusLabel: 'User status',
                statusActive: 'Active',
                statusBlocked: 'Blocked',
                statusPending: 'Pending',
                firstName: 'First name',
                lastName: 'Last name',
                email: 'Email',
                loginStatus: 'Login status',
                resendEmail: 'Resend activation email',
                notActivated: 'Login is not activated yet',
                APIkey: 'API key',
                active: 'Active',
                emailSent: 'The activation email has been resent',
                roleLabel: 'Role',
                loginMethod: {
                    title: 'Login method',
                    password: 'Username and password',
                    ssoBased: 'Log in with {{name}}',
                },
                noRoleConfiguration: 'There is nothing to configure for this application role',
                selectApplicationRole: 'Select an application role',
                invalidProfileData: 'Profile data is invalid',
                accountStatusMessage: {
                    pending:
                        'This user cannot login because the account is pending. The user needs to activate their account first.',
                    blocked: 'This user cannot login because the account is blocked.',
                },
                firstNameRequired: 'A first name is required',
                lastNameRequired: 'A last name is required',
                userApprovedToastMessage: '{{firstName}} {{lastName}} has been approved.',
                userBlockedToastMessage: '{{firstName}} {{lastName}} has been blocked.',
                userUnblockedToastMessage: '{{firstName}} {{lastName}} has been unblocked.',
            },
            userListPage: {
                title: 'Users',
                idHeader: 'ID',
                statusHeader: 'Status',
                nameHeader: 'Name',
                roleHeader: 'Role',
                activationButton: 'Resend Activation Link',
                triggerPasswordResetButton: 'Trigger password reset',
            },
            editors: {
                chooseEntity: 'Choose {{type}}',
                currentEntity: 'Current selection',
            },
            userErrorCode: {
                emailIsDuplicate: 'Email address already in use in one of your other logins.',
                emailIsNotValid: 'Invalid email address.',
                userDoesNotExist: "User doesn't exist.",
                userDoesNotHavePermissions: 'The current user does not have permission to execute this operation.',
                invalidSsoLogin: 'Single Sign-On configuration not found.',
                unexpected: 'An unexpected error occureed.',
            },
            auth: {
                emailduplicate: 'Email address already in use in one of your other logins',
                emailinvalid: 'Invalid email address',
                emailinuse: 'Email address already in use by another user',
                loginincomplete: 'Incomplete login details',
                usernotexist: "User doesn't exist",
                applicationnoexists: "Application doesn't exist",
                userblocked: 'User blocked',
                userpending: 'User is pending approval',
                usernotactive: 'User is not active',
                passwordinvalid: 'Password is incorrect',
                totpinvalid: 'Code is invalid',
                totprequired: 'Enter your two-factor code',
                keymissing: 'Key is missing',
                applicationidmissing: 'Application Id is missing',
                unknownincorrectdomain: 'Unknown or incorrect domain',
                token: {
                    invalid: 'Invalid token',
                },
                roleRequired: 'Role is a required field',
                roleNotFound: 'Role not found',
                loginBlocked: 'This login is blocked. Please contact support if you think this is a mistake.',
                loginAlreadyActive: 'This login has already been activated.',
                noActivatedEmail: "You don't have an activated email address.",
                sso: {
                    'sso-provider-error':
                        'An error occurred while authenticating with the Single Sign-On provider. If this error persists, please contact your administrator.',
                    'internal-error':
                        'An internal error occurred while authenticating with the Single Sign-On provider. If this error persists, please contact your administrator.',
                    'user-not-linked':
                        'Single Sign-On is not activated. Please check your email or contact your administrator.',
                    'sso-configuration-not-found': 'Single Sign-On configuration not found',
                    'sso-token-expired': 'You have been logged out due to inactivity. Please log in again.',
                },
            },
            fileFormatter: {
                dropzoneTextSingle: 'Drop a file here, or click to select',
                dropzoneTextMultiple: 'Drop your files here, or click to select',
            },
            pluginFormatter: {
                failedLoading: "Error: couldn't load plugin",
                pluginCrashed: 'Error: the plugin crashed. Reload the page to try again.',
            },
        },
    },
    nl: {
        translation: {
            loading: {
                checkingLogin: 'Inloggegevens controleren...',
                loadingApplication: 'Applicatie laden...',
                loadingInterface: 'Interface laden...',
                loginScreen: 'Niet ingelogd, navigeren naar inlogvenster...',
                invalidDomain: 'Geen applicatie gevonden op dit domein.',
                loadingError: 'Fout tijdens laden van applicatie',
                pageLoading: 'Laden...',
            },

            generalShared: {
                submitButton: 'Verzenden',
                loading: 'Laden...',
                editWithFormatter: 'Bewerken - {{formatterName}}',
                edit: 'Bewerken',
                delete: 'Verwijderen',
                disable: 'Uitschakelen',
                clear: 'Wissen',
                clearAll: 'Alles wissen',
                save: 'Opslaan',
                addAnother: 'Opslaan en nog een',
                cancel: 'Annuleren',
                close: 'Sluiten',
                add: 'Toevoegen',
                addNew: 'Nieuw',
                apply: 'Toepassen',
                download: 'Downloaden',
                view: 'Bekijken',
                areYouSure: 'Weet u het zeker?',
                yesImSure: 'Ja, ik weet het zeker',
                exportToExcel: 'Exporteer naar Excel',
                exportToExcelRunning: 'Export is bezig, even geduld',
                cannotDelete: 'Data kan niet worder verwijderd, wordt elders gebruikt',
                none: 'Geen',
                saved: 'Opgeslagen',
                peek: 'Bekijk',
                openInThisWindow: 'Open in deze tab',
                openInNewWindow: 'Open in nieuwe tab',
                filter: 'Filter op {{columnName}}',
                time: 'Tijd',
                back: 'Terug',
                reload: 'Pagina verversen',
                dismiss: 'Annuleren',
                selectAll: 'Selecteer alles',
                deSelecttAll: 'Deselecteer alles',
                remove: 'Verwijder',
                copyPassword: 'Wachtwoord kopiëren',
            },
            signalRStatus: {
                connecting:
                    'Verbinding maken met de server... Live updates zullen niet getoond worden totdat de verbinding hersteld is.',
                disconnected:
                    'Verbinding met de server is verbroken. Ververs de pagina om opnieuw te verbinden. Live updates zullen niet getoond worden totdat de verbinding hersteld is.',
                reconnecting:
                    'De verbinding met de server is verbroken, we proberen de verbinding te herstellen. Live updates zullen niet getoond worden totdat de verbinding hersteld is.',
                reconnected: 'De verbinding is hersteld, je bent weer online.',
                failedToConnect: 'Verbinding maken met de server is mislukt',
            },

            versionUpdate: {
                major: 'Een ingrijpende update is beschikbaar! Ververs de pagina snel, de applicatie zal mogelijk niet werken totdat de pagina vervest is.',
                minor: 'Een nieuwe versie is beschikbaar! Ververs de pagina om de nieuwe functies te gebruiken.',
                patch: 'Een kleine versie-update is beschikbaar! Ververs wanneer je een moment hebt de pagina om de nieuwe versie te gebruiken.',
                revision:
                    'Een kleine versie-update is beschikbaar! Ververs wanneer je een moment hebt de pagina om de nieuwe versie te gebruiken.',
            },

            errors: {
                notfound: '404 - gegevens niet gevonden.',
                unauthorized: '401 - Je moet ingelogd zijn om deze data te bekijken.',
                forbid: '403 - Je hebt geen toegang tot deze data.',
                general: 'Er ging iets mis.',
                entitygone: '410 - Deze data is verwijderd.',
                required: 'Dit veld is verplicht',
                minLength: 'Het ingevulde veld is te kort',
                invalidInputLayout: 'Ongeldig formaat',
                notAnArray: 'Moet een lijst zijn',
                notAnObject: 'Moet een object zijn',
                notAValue: 'Moet een waarde zijn',
                notAnFk: 'Moet een referentie zijn',
                invalidFK: 'Ongeldige referentie',
                invalidEnum: 'Ongeldige optie',
                notAString: 'Moet tekst zijn',
                notAnInteger: 'Moet een getal zijn',
                notADecimal: 'Moet een decimaal getal zijn',
                notABoolean: 'Moet een boolean zijn',
                notADate: 'Moet een datum zijn',
                errorLoadingEntities: 'Het laden van de data is mislukt. Probeer het opnieuw, of pas de filters aan',
                actionNotAllowed: 'Je mag deze actie niet uitvoeren',
                invalidEmail: 'Moet een geldig emailadres zijn',
            },
            dateTimeFilter: {
                before: 'Voor',
                after: 'Na',
                between: 'Tussen',
                empty: 'Leeg',
            },
            numericValuesFilter: {
                lessThen: 'kleiner dan',
                greaterThen: 'groter dan',
                equal: 'gelijk aan',
            },
            booleanValuesFilter: {
                true: 'Ja',
                false: 'Nee',
            },
            enumRadioFilter: {
                noOptions: 'Geen opties',
            },
            searchableDropdown: {
                placeholder: 'Type om te zoeken',
                noOptionFormatter: 'Dit filter is verkeerd geconfigureerd, neem contact op met je beheerder',
            },
            accountShared: {
                userNameLabel: 'Gebruikersnaam',
                passwordLabel: 'Wachtwoord',
                totpLabel: 'TOTP code',
                backToLogin: 'Ga naar het loginscherm.',
                enterPassword: 'Voeg hieronder uw nieuwe wachtwoord in.',
                passSetError: 'Kan wachtwoord niet opslaan',
            },
            loginscreen: {
                welcome: 'Welkom bij',
                title: '{{applicationName}}',
                loginError: 'Kan niet inloggen',
                signInButton: 'Inloggen',
                forgotPasswordButton: 'Wachtwoord vergeten?',
                forgotTwoFactorButton: 'Two-factor code kwijt?',
                activeTwoFactorButton: 'Two-factor activeren',
                addNewEmailButton: 'Nieuw e-mailadres toevoegen',

                sso: {
                    or: 'of',
                    continueWith: 'Doorgaan met {{provider}}',
                    error: 'Er ging iets mis tijdens het inloggen met {{provider}}. Probeer het opnieuw.',
                },
            },
            forgotPassword: {
                title: 'Bent u uw wachtwoord vergeten?',
                titleAfter: 'Controleer uw inbox',
                description: 'Voeg hieronder uw gebruikersnaam in, en wij versturen u een mail met verdere instructies',
                passResetError: 'Kon geen wachtwoord herstel email versturen',
                messageAfter: 'We hebben een mail gestuurd met verdere instructies. U kunt dit scherm sluiten',
            },
            forgotPasswordAndTotp: {
                title: 'Accountgegevens kwijt?',
                description:
                    'U hebt aangegeven dat u zowel uw wachtwoord als uw twee-factor code kwijt bent. Helaas is het dan niet mogelijk om op een automatische wijze toegang tot het account te herstellen. Neem contact op met uw systeembeheerder.',
            },
            recoverPassword: {
                title: 'Voer een nieuw wachtwoord in',
                recoverError: 'Kan wachtwoord niet instellen',
                titleAfter: 'Wachtwoord opgeslagen',
                messageAfter: 'We hebben uw nieuwe wachtwoord opgeslagen.',
            },
            activateAccount: {
                title: 'Activeer uw account',
                titleAfter: 'Account geactiveerd',
                messageAfter: 'Bedankt voor het activeren van uw account. U kunt nu inloggen.',
            },
            setupTwoFactor: {
                title: 'Tweefactorauthenticatie is vereist voor je account',
                subtitle:
                    "Om je account veiliger te houden, is het gebruik van tweefactorauthenticatie verplicht. Klik op '2FA aanzetten' om dit in te stellen",
                generateSecretBtn: '2FA aanzetten',
                totpGeneratedDescription:
                    'De tweefactorauthenticatie is aangezet. Configureer je account in een geschikte authenticator-app (zoals Google Authenticator) met behulp van de onderstaande code.',
                toLoginBtn: 'Naar login',
                totpLabel: 'Geheime code',
            },
            forgotTwoFactor: {
                title: 'Je 2FA code kwijt?',
                description:
                    'Als je geen toegang meer hebt tot de tweefactorauthenticatie, dan kun je je toegang herstellen door middel van een email. Als je op de onderstaande knop klikt, ontvang je een email met verdere instructies voor het herstel van je account.',
                sendRecoveryEmailBtn: 'Herstel-email verzenden',
                sendRecoveryEmailError: 'We konden geen email sturen',
                titleAfter: 'Je hebt mail',
                messageAfter: 'We hebben een herstel-email gestuurd, controleer je inbox.',
            },
            resetTwoFactor: {
                loadingTitle: 'Twee-factor herstellen...',
                successTitle: 'Voltooid',
                errorTitle: 'Er ging iets mis',
                loadingDescription: 'De twee-factor autenticatie wordt uitgezet op jouw account. Een ogenblik geduld.',
                errorDescription:
                    'Het uitzetten van de tweefactorauthenticatie is mislukt. De foutmelding is: {{error}}. Probeer het opnieuw, of neem contact op met een beheerder.',
                successDescription:
                    'Tweefactorauthenticatie is uitgezet op jouw account. Klik op de onderstaande link "Naar loginscherm" om naar het loginscherm te gaan. Daar kun je inloggen met je e-mailadres en wachtwoord.',
            },
            sso: {
                activateTitle: 'Registreren met {{provider}}',
                activateSubtitle: 'Er is een nieuw {{applicationName}} account voor u aangemaakt.',
                activateDescription:
                    'Klik op onderstaande knop om in te loggen met uw {{provider}} account en daarmee uw account te activeren.',

                linkTitle: 'Inloggen met {{provider}}',
                linkSubtitle:
                    'U heeft aangegeven om met {{provider}} in te willen kunnen loggen bij {{applicationName}}.',
                linkDescription:
                    'Klik op onderstaande knop om in te loggen met uw {{provider}} account en daarmee uw account te koppelen.',

                unknownConfiguration: 'Onbekende Single Sign-On configuratie',

                loginWithProviderFailed: 'Inloggen met {{provider}} is mislukt',
                loginWithProviderSuccess: 'Inloggen met {{provider}} is gelukt',
                backToLogin: 'Terug naar inlogscherm',
                noSsoProviders: 'Er zijn geen Single Sign-On-providers geregistreerd voor deze applicatie',
            },
            confirmButton: {
                title: 'Weet u het zeker?',
                yes: 'Ja, ik weet het zeker',
                no: 'Nee',
                cancel: 'Annuleren',
                back: 'Ga terug',
            },
            emptyStatePage: {
                title: 'Maak een keuze uit het menu aan de linkerkant',
            },
            entitiesList: {
                error: 'Fout: geen lijstweergave geconfigureerd voor dit type',
                exportButton: 'Exporteer naar Excel',
            },
            entityCreatePage: {
                title: '- nieuw',
            },
            entityDependencies: {
                title: 'Gelinkte gegevens',
            },
            entityDetailPage: {
                title: '- details',
                error: 'Fout: geen detail weergave configuratie voor dit type',
                noFormatters: 'Fout: geen detailweergave mogelijk voor deze data',
                formatterNotAvailable: 'Fout: deze pagina is niet beschikbaar voor deze data',
                chooseFormatter: 'Kies de weergave van deze data',
                backTo: 'Naar lijst van',
                entityOutdated: 'De gegevens op het scherm zijn verouderd',
                reloadButton: 'Herladen',
            },
            entitymeta: {
                creation: 'Gemaakt door {{who}} op {{when, datetime}}',
                edited: 'Bewerkt door {{who}} op {{when, datetime}}',
            },
            entityEditForm: {
                title: '- bewerken',
                error: 'Fout: geen editor configuratie voor dit type',
                chooseFormatter: 'Kies de bewerkpagina',
                notAllowed: 'U heeft geen toegang tot deze pagina',
            },
            filterPanel: {
                addFilter: 'Voeg filter toe',
                advancedFilters: 'Geavanceerd filteren',
                chooseFilter: 'Kies filters',
                applyFilter: 'Pas filter toe',
                refresh: 'Ververs data',
                openAccordion: 'Bekijk filteropties',
                search: 'Zoeken',
            },
            filterPopup: {
                title: 'Filter op {{attributeName}} - ({{selectedCount}} geselecteerd)',
                applyButton: 'Toepassen',
                selected_zero: 'Geen geselecteerd',
                selected_one: 'Een geselecteerd',
                selected_other: '{{count}} geselecteerd',
            },
            accountSettings: {
                title: 'Mijn accountinstellingen',
                userInfo: 'Gebruikersinformatie',
                userNamePassword: 'Gebruikersnaam en wachtwoord',
                emailLabel: 'E-mailadres',
                passwordLabel: 'Wachtwoord',
                apiLogin: 'API sleutel',
                key: 'Sleutel',
                copyKey: 'Sleutel kopiëren',
                twoFactorTitle: 'Tweefactorauthenticatie',
                activateTotp: 'Tweefactor activeren',
                showQR: 'QR-code tonen',
                disableTotp: 'Tweefactor uitzetten',
                totpActive: 'Tweefactor actief',
                totpInactive: 'Tweefactor inactief',
                twoFactorEnabledDescription:
                    "Tweefactorauthenticatie is ingesteld op uw account. Klik op de 'Geheime code weergeven' knop om uw account toe te voegen aan een andere authenticatie-app.",
                twoFactorDisableDescription:
                    'U kunt de tweefactorauthenticatie uitzetten door op de onderstaande knop te klikken. Let op: hierna is uw account minder veilig.',
                twoFactorCallToAction:
                    'U kunt uw account veiliger maken door tweefactorauthenticatie in te stellen. Zorg ervoor dat u een geschikte applicatie hiervoor bij de hand hebt, zoals Google Authenticator. Klik daarna op de onderstaande knop.',
                twoFactorCreateButton: 'Tweefactorauthenticatie instellen',
                twoFactorDisableButton: 'Tweefactorauthenticatie uitzetten',
                twoFactorShowSecret: 'Geheime code weergeven',
                twoFactorSecretDescription:
                    'Tweefactorauthenticatie staat nu aan op uw account. Scan de onderstaande QR code met de applicatie die u wilt gebruiken.',
                qrCodeWarning:
                    'Waarschuwing! Scan deze QR code onmiddellijk, deze wordt maar een keer getoond. Als u deze code niet scant, kunt u niet meer inloggen.',
                twoFactorHideSecret: 'Code verbergen',
                noPasswordLogins: 'Er zijn geen gebruikersnaam-wachtwoord logins geconfigureerd voor uw account',
                noApiLogins: 'Er zijn geen API sleutels geconfigureerd voor uw account',
                hide: 'Verberg',
                show: 'Toon',
                userStatuses: {
                    Active: 'Actief',
                    Blocked: 'Geblokkeerd',
                    Pending: 'In afwachting',
                },
                statusActive: 'Actief',
                statusBlocked: 'Geblokkeerd',
                statusPending: 'In afwachting',
                statusUnlinked: 'Niet gekoppeld',
                sso: {
                    title: 'Single Sign-On',
                    noLogins: 'Er zijn geen Single Sign-On logins geconfigureerd voor uw account',
                    link: 'Koppel {{provider}} account',
                    remove: 'Verwijder {{provider}} login',
                    confirmRemove:
                        'Weet u zeker dat u de Single Sign-On login met provider <strong>{{provider}}</strong> en email <strong>{{email}}</strong> wilt verwijderen? U kunt dan niet meer inloggen met dit <strong>{{provider}}</strong> account.',
                    confirmRemoveExtra:
                        'U wordt uitgelogd en moet een andere inlogmethode gebruiken om opnieuw in te loggen.',

                    loginAdded: 'Login toegevoegd. Er is een mail naar u gestuurd met instructies om verder te gaan.',
                    activeLoginRemoved: 'Login verwijderd. U wordt nu uitgelogd.',
                    inactiveLoginRemoved: 'Login verwijderd.',
                    emailSent: 'Email verstuurd. Open de mail in uw mailbox om verder te gaan.',
                },
                loginMethodNotExist: 'Kan aanmeldingsmethode niet vinden.',
                passwordLoginDelete: {
                    confirmTitle: 'E-mail/wachtwoord login verwijderen?',
                    confirmRemove:
                        'Weet je zeker dat je je email/wachtwoord aanmeldmethode wilt verwijderen? ' +
                        'Je zal niet langer {{email}} kunnen gebruiken om in te loggen bij {{application}}.',
                    noOtherLoginMethods: 'De gebruiker moet minstens één SSO-loginmethode hebben.',
                },
                ssoLoginDelete: {
                    confirmTitle: 'Verwijderen {{provider}} login?',
                    confirmRemove:
                        'Weet je zeker dat je de Single Sign-On login met provider {{provider}} en e-mail {{providerEmail}} wilt verwijderen? ' +
                        'Ze zullen niet langer {{provider}} kunnen gebruiken om in te loggen op {{application}}.',
                    noOtherLoginMethods: 'De gebruiker moet minstens één ander wachtwoord of SSO-loginmethode hebben.',
                    ssoNotFound: 'SSO-configuratie niet gevonden.',
                },
                apiLoginDelete: {
                    confirmTitle: 'API-sleutel verwijderen?',
                    confirmRemove:
                        'Weet je zeker dat je deze API-sleutel wilt verwijderen? ' +
                        'Toepassingen van derden die deze API-sleutel gebruiken om verbinding te maken met {{application}} zullen niet langer toegang hebben.',
                },
                loading: {
                    addingLogin: 'Login toevoegen',
                    removingSsoLogin: 'Single Sign-On login verwijderen',
                    removingPasswordLogin: 'Email/wachtwoord loginmethode verwijderen',
                    removingApiLogin: 'Api-login verwijderen',
                    sendingEmail: 'Email versturen',
                    addingApiKey: 'API-sleutel login toevoegen',
                    addingPasswordlogin: 'e-mail/wachtwoord loginmethode toevoegen',
                },
                success: {
                    passwordLoginDeleted:
                        'Je email/wachtwoord loginmethode is verwijderd. ' +
                        'Je zal niet langer {{email}} kunnen gebruiken om in te loggen bij {{application}}.',
                    passwordLoginAdded:
                        'Je email/wachtwoord loginmethode is toegevoegd. ' +
                        'Je ontvangt een activatie-e-mail om een wachtwoord in te stellen op {{email}}.',
                    ssoLoginDeleted:
                        'De Single Sign-On login met provider {{provider}} en email {{providerEmail}} is verwijderd. ' +
                        'Ze zullen niet langer {{provider}} kunnen gebruiken om in te loggen op {{application}}.',
                    apiLoginDeleted:
                        'Je API-sleutel is verwijderd. ' +
                        'Toepassingen van derden die deze API-sleutel gebruiken om verbinding te maken met {{application}} zullen niet langer toegang hebben.',
                    apiLoginAdded: 'Je API-sleutel is toegevoegd',
                },
            },
            modalSsoLoginAdd: {
                title: 'Single Sign-On login toevoegen',
                ssoProvider: 'Single Sign-On aanbieder',
                descriptionManyProviders:
                    'De uitnodigingslink om een Single Sign-On login aan te maken via de geselecteerde provider wordt naar het opgegeven e-mailadres gestuurd:',
                descriptionOneProvider:
                    'De uitnodigingslink om een Single Sign-On login aan te maken via <strong>{{providerName}}</strong> wordt verzonden naar het opgegeven e-mailadres:',
                selectSsoProvider: 'Kies een Single Sign-On aanbieder',
            },
            modalPasswordLoginAdd: {
                title: 'Email/Password loginmethode toevoegen',
                description:
                    'Vul hieronder het e-mailadres in waarmee je wilt kunnen inloggen. Er zal een e-mail verstuurd worden naar dit e-mailadres met een activatielink, klik op die link om een wachtwoord in te stellen en de login te activeren.' +
                    '<p><strong>Let op:</strong> wanneer je op de activatielink klikt, zal je uitgelogd worden uit je huidige sessie.</p>',
            },
            nav: {
                menuCategoryUsers: 'Gebruikers',
                menuLabelUsers: 'Gebruikers',
                menuCategoryAccount: 'Account',
                menuLabelSignOut: 'Afmelden',
                menuLabelSettings: 'Mijn accountinstellingen',
                menuSwitchAdmin: 'Schakel over naar admin',
                menuSwitchData: 'Schakel over naar data',
                viewDetail: 'bekijk →',
                viewDetailPage: 'bekijk detailpagina →',
                chooseRole: 'Gebruikersrol kiezen',
                userRole: 'Gebruikersrol',
                adminSettings: 'Admin gegevens',
                navigation: 'Navigatie',
                state: {compact: 'Menu uitklappen', normal: 'Menu inklappen'},
                theme: 'Thema',
                themeMode: {light: 'Licht', dark: 'Donker'},
            },
            paging: {
                noMoreToLoadView:
                    '$t(paging.showingFormatted, {"count": {{showingFormatted}}}) $t(paging.total, {"count": {{total}}})',
                loadMore:
                    'Meer laden $t(paging.showing, {"count": {{showing}}}) $t(paging.total, {"count": {{total}}})',
                showingFormatted: '{{count}} getoond',
                showing: '- {{count}} getoond',
                total_zero: '',
                total_other: ' van {{count}}',
            },
            arrayEditor: {
                addNew: 'Voeg nieuwe {{typeName}} toe',
            },
            textboxFilter: {
                containsValue: 'Bevat',
                equalsValue: 'Gelijk aan',
                startsWithValue: 'Begint met',
            },
            boolEditor: {
                yes: 'Ja',
                no: 'Nee',
            },
            entityTable: {
                noResults: 'Er zijn geen overeenkomende gegevens',
                saveColumnSelection: 'Opslaan',
                noColumns: 'Er kunnen geen gegevens worden weergegeven omdat deze pagina geen kolommen heeft',
            },
            userCreateModal: {
                title: 'Nieuwe gebruiker toevoegen',
                firstNameInputLabel: 'Voornaam',
                lastNameInputLabel: 'Achternaam',
                loginSettingDropdownLabel: 'Login instellingen',
                loginSettingDropdownOptions: {
                    apiKey: 'API-sleutel',
                    usernamePassword: 'Gebruikersnaam en wachtwoord',
                    sso: 'SSO',
                },
                ssoProvidersRadioLabel: 'Inloggen met',
                userRoleDropdownLabel: 'Gebruikersrol(len)',
                userRoleDropdownPlaceholder: 'Kies een of meer gebruikersrol(len)',
                userRoleDropdownConfigurationsRequiredTitle: 'Configuratie vereist!',
                userRoleDropdownConfigurationsRequiredText:
                    'Je moet een of meer van de geselecteerde rollen nog configureren. Dat kan na het toevoegen van de gebruiker. Tot die tijd worden deze rollen nog niet toegewezen.',
                userRoleDropdownNoConfigurations: 'Geen van de geselecteerde rollen vereist configuratie',
                userRoleDropdownNoConfiguration: 'De geselecteerde rol vereist geen configuratie',
                successToastMessage: 'Gebruiker {{firstName}} {{lastName}} is succesvol toegevoegd',
                successToastMessageWithConfig:
                    'Gebruiker {{firstName}} {{lastName}} is toegevoegd, maar een of meer geselecteerde rollen missen configuraties. Zorg ervoor dat je de configuraties toevoegt om het proces te voltooien.',
                applicationRoleRequired: 'Kies tenminste een rol',
                chooseApplicationRole: 'Een rol is vereist',
                firstNameRequired: 'Vul de voornaam in',
                lastNameRequired: 'Vul de achternaam in',
                emailRequired: 'Vul het e-mailadres in',
            },
            userDetailPage: {
                title: 'Gebruikersdetail',
                backToList: 'Terug naar lijst',
                roleNotFound: 'Rol niet gevonden',
                userInfo: 'Gebruikersinformatie',
                userName: 'Gebruikersnaam',
                userStatus: 'Gebruikersstatus',
                userRegistrationStatus: 'Registratiestatus',
                approveUser: 'Gebruiker goedkeuren',
                neverLoggedIn: 'nog niet ingelogd',
                lastLogin: 'laatste login: {{loginTime}}',
                blockUser: 'Blokkeer gebruiker',
                unblockUser: 'Deblokkeer gebruiker',
                approveUserConfirmText: 'Weet u zeker dat u deze gebruiker wilt goedkeuren?',
                blockUserConfirmText: 'Weet je zeker dat je deze gebruiker wilt blokkeren?',
                unBlockUserConfirmText: 'Weet je zeker dat je deze gebruiker wilt deblokkeren?',
                applicationRoles: 'Applicatierollen',
                assignApplicationRole: 'Applicatierol toewijzen',
                applicationRoleNotConfigured: 'Configuratie ontbreekt: rol is nog niet toegewezen',
                noApplicationRoles:
                    'Er zijn geen rollen toegekend aan deze gebruiker. Deze gebruiker kan doordoor niet inloggen.',
                noProfileFormatter: 'De instellingen van deze applicatierol kunnen niet aangepast worden',
                deleteUserSuccessMessage: 'De gebruiker is verwijderd',
                successToastMessage: 'De geselecteerde rollen zijn toegewezen aan {{firstName}} {{lastName}}.',
                successToastMessageWithConfig:
                    'De geselecteerde rollen zijn toegewezen aan {{firstName}} {{lastName}}, maar een of meer rollen hebben geen configuratie. Zorg ervoor dat je de configuratie toevoegt om het proces te voltooien.',
                noDetailFormatter:
                    'De gegevens kunnen niet worden weergegeven. Er is geen detailformatter voor het profiel van deze rol of er zijn geen attributen toegevoegd aan de formatter.',
                noConfigurableAttributes:
                    'De gegevens kunnen niet worden bewerkt. Er zijn geen attributen toegevoegd aan de bewerkformatter van het profiel van deze rol.',
                editProfileData: 'Profielgegevens bewerken',
                applicationRoleAdded: 'Applicatierol toegevoegd',
                applicationRoleDeleted: 'Applicatierol verwijderd',
                profileDataSaved: 'Profielgegevens opgeslagen',
                loginSettings: {
                    title: 'Login instellingen',
                    apiKeyLogin: 'API-sleutel Login',
                    noApiKeyLogins: 'Er zijn geen API-sleutel logins geconfigureerd voor dit account',
                    deleteApiKey: {
                        confirmTitle: 'API sleutel verwijderen?',
                        confirmRemove:
                            'Weet je zeker dat je deze API-sleutel wilt verwijderen voor {{userDisplayName}}?' +
                            ' Toepassingen van derden die deze API-sleutel gebruiken om verbinding te maken met {{application}} zullen niet langer toegang hebben.',
                    },
                    sso: {
                        title: 'Single Sign-On',
                        noLogins: 'Er zijn geen Single Sign-On logins geconfigureerd voor dit account',
                        remove: '{{provider}} login verwijderen',
                        confirmRemove:
                            'Weet je zeker dat je de Single Sign-On login met provider <strong>{{provider}}</strong> en emailadres <strong>{{email}}</strong> voor <strong>{{userDisplayName}}</strong> wilt verwijderen?' +
                            ' Ze kunnen dit <strong>{{provider}}</strong> account niet meer gebruiken om zich aan te melden.',
                        confirmRemoveExtra:
                            'De gebruiker wordt afgemeld en moet een andere aanmeldingsmethode gebruiken om zich opnieuw aan te melden.',
                        emailSent: 'Email verzonden. De gebruiker moet zijn mailbox controleren om verder te gaan.',
                        loginAdded:
                            'Login toegevoegd. Er werd een email gestuurd naar {{emailAddress}} om het te activeren.',
                        loginRemoved: 'Login succesvol verwijderd.',
                    },
                },
                loading: {
                    addingLogin: 'Login toevoegen',
                    removingSsoLogin: 'Single Sign-On login verwijderen',
                    removingPasswordLogin: 'Email/wachtwoord loginmethode verwijderen',
                    removingApiLogin: 'API-sleutel login verwijderen',
                    sendingEmail: 'Email versturen',
                    addingApiKey: 'API-sleutel login toevoegen',
                    addingPasswordlogin: 'Email/wachtwoord loginmethode toevoegen',
                },
                success: {
                    passwordLoginDeleted:
                        'De email/wachtwoord loginmethode voor {{userDisplayName}} is verwijderd. ' +
                        'Ze zullen niet langer {{email}} kunnen gebruiken om in te loggen bij {{application}}.',
                    passwordLoginAdded:
                        'Je e-mail/wachtwoord loginmethode is toegevoegd. ' +
                        'Je ontvangt een activatie-e-mail om een wachtwoord in te stellen op {{email}}.',
                    ssoLoginDeleted:
                        'De Single Sign-On login met provider {{provider}} en email {{providerEmail}} is verwijderd. ' +
                        'Ze zullen niet langer {{provider}} kunnen gebruiken om in te loggen bij {{application}}.',
                    apiLoginDeleted:
                        'De API-sleutel van {{userDisplayName}} is verwijderd. ' +
                        'Toepassingen van derden die deze API-sleutel gebruiken om verbinding te maken met {{application}} zullen niet langer toegang hebben.',
                    apiLoginAdded: 'De API-sleutel voor {{userDisplayName}} is toegevoegd.',
                },
            },
            userEditPage: {
                title: '- bewerken',
                backToList: 'Terug naar lijst',
            },
            userRegistrationStatus: {
                completed: 'Voltooid',
                pendingApproval: 'Wachtend op goedkeuring',
                pendingLogin: 'Wachtend op login-activatie',
                pendingRole: 'Wachtend op roltoewijzing',
                revoked: 'Ingetrokken',
            },
            userEditForm: {
                basicInfo: 'Basisgegevens',
                profileInfo: 'Profielgegevens',
                applicationRoleConfiguration: 'Rolconfiguratie',
                loginInfo: 'Logingegevens',
                loginTypeLabel: 'Type login',
                loginTypeUsername: 'Gebruikersnaam en wachtwoord',
                loginTypeApi: 'API-sleutel',
                loginTypeOpenId: 'OpenId Connect',
                emailLabel: 'E-mailadres',
                applicationRoleLabel: 'Applicatierol',
                roleDropdown: '-- kies een rol --',
                statusLabel: 'Gebruiker status',
                statusActive: 'Actief',
                statusBlocked: 'Geblokkeerd',
                statusPending: 'In afwachting',
                firstName: 'Voornaam',
                lastName: 'Achternaam',
                email: 'Email',
                loginStatus: 'Login status',
                resendEmail: 'Activatie-email opnieuw sturen',
                notActivated: 'Login is nog niet geactiveerd',
                APIkey: 'API-sleutel',
                active: 'Actief',
                emailSent: 'De activatie-email is opnieuw verstuurd',
                roleLabel: 'Rol',
                loginMethod: {
                    title: 'Login methode',
                    password: 'Gebruikersnaam en wachtwoord',
                    ssoBased: 'Log in met {{name}}',
                },
                noRoleConfiguration: 'Er is niets te configureren voor deze applicatierol',
                selectApplicationRole: 'Kies een applicatierol',
                invalidProfileData: 'Profielgegevens zijn ongeldig',
                accountStatusMessage: {
                    pending:
                        'Deze gebruiker kan niet inloggen omdat het account in afwachting is. De gebruiker moet zijn account eerst activeren.',
                    blocked: 'Deze gebruiker kan niet inloggen omdat het account geblokkeerd is.',
                },
                firstNameRequired: 'Vul de voornaam in',
                lastNameRequired: 'Vul de achternaam in',
                userApprovedToastMessage: '{{firstName}} {{lastName}} is goedgekeurd.',
                userBlockedToastMessage: '{{firstName}} {{lastName}} is geblokkeerd',
                userUnblockedToastMessage: '{{firstName}} {{lastName}} is deblokeerd',
            },
            userListPage: {
                title: 'Gebruikers',
                idHeader: 'ID',
                statusHeader: 'Status',
                nameHeader: 'Naam',
                roleHeader: 'Rol',
                activationButton: 'Activeringslink opnieuw verzenden',
                triggerPasswordResetButton: 'Activeer wachtwoordherstel',
            },
            editors: {
                chooseEntity: 'Kies {{type}}',
                currentEntity: 'Huidige selectie',
            },
            userErrorCode: {
                emailIsDuplicate: 'E-mailadres wordt al gebruikt in een van je andere login.',
                emailIsNotValid: 'Ongeldig emailadres.',
                userDoesNotExist: 'Gebruiker bestaat niet.',
                userDoesNotHavePermissions:
                    'De huidige gebruiker heeft geen toestemming om deze bewerking uit te voeren.',
                invalidSsoLogin: 'Single Sign-On configuratie niet gevonden.',
                unexpected: 'Er is een onverwachte fout opgetreden.',
            },
            auth: {
                emailduplicate: 'E-mailadres wordt al gebruikt in een van je andere login',
                emailinuse: 'E-mailadres wordt al door een andere gebruiker gebruikt',
                emailinvalid: 'Ongeldig emailadres',
                loginincomplete: 'Onvolledige inloggegevens',
                usernotexist: 'Gebruiker bestaat niet',
                applicationnoexists: 'Applicatie bestaat niet',
                userblocked: 'Gebruiker geblokkeerd',
                userpending: 'Gebruiker nog niet geaccepteerd',
                usernotactive: 'Gebruiker niet actief',
                passwordinvalid: 'Wachtwoord niet juist',
                totpinvalid: 'Code is ongeldig',
                totprequired: 'Vul uw twee-factor code in',
                keymissing: 'Sleutel ontbreekt',
                applicationidmissing: 'Application Id ontbreekt',
                unknownincorrectdomain: 'Onbekend of onjuist domein',
                token: {
                    invalid: 'Ongeldige token',
                },
                roleRequired: 'Rol is een verplicht veld',
                roleNotFound: 'Rol niet gevonden',
                loginBlocked: 'Deze login is geblokkeerd. Neem contact op met support als u denkt dat dit een fout is.',
                loginAlreadyActive: 'Deze login is al geactiveerd.',
                noActivatedEmail: 'U heeft geen geactiveerd emailadres.',
                sso: {
                    'sso-provider-error':
                        'Er is een fout opgetreden tijdens het authenticeren met de Single Sign-On provider. Als deze fout aanhoudt, neem dan contact op met uw beheerder.',
                    'internal-error':
                        'Er is een interne fout opgetreden tijdens het authenticeren met de Single Sign-On provider. Als deze fout aanhoudt, neem dan contact op met uw beheerder.',
                    'user-not-linked':
                        'Single Sign-On is niet geactiveerd. Controleer uw email of neem contact op met uw beheerder.',
                    'sso-configuration-not-found': 'Single Sign-On configuratie niet gevonden',
                    'sso-token-expired': 'Je bent uitgelogd vanwege inactiviteit. Log alstublieft opnieuw in.',
                },
            },
            fileFormatter: {
                dropzoneTextSingle: 'Zet hier een bestand neer, of klik om te selecteren',
                dropzoneTextMultiple: 'Zet uw bestanden hier neer, of klik om te selecteren',
            },
            pluginFormatter: {
                failedLoading: 'Fout: kon plugin niet laden.',
                pluginCrashed: 'Fout: de plugin is gecrashed. Herlaad de pagina om het opnieuw te proberen.',
            },
        },
    },
};

i18n.use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        fallbackLng: 'en',

        interpolation: {
            escapeValue: false,
        },

        detection: {
            order: ['localStorage', 'navigator'],
            lookupLocalStorage: 'i18nextLng',
        },
    });

export default i18n;
